/**
 * PatchToolbar - Floating Selection Toolbar
 *
 * Appears when user selects text in the draft editor.
 * Provides "Patch..." prompt input for LLM-powered inline rewrites,
 * plus Copy and Delete actions.
 *
 * @module @humanizer/studio/components/workspace/PatchToolbar
 */

import React, { useCallback, useRef, useState } from 'react';
import { useApi } from '../../contexts/ApiContext';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface PatchToolbarProps {
  selectedText: string;
  anchorRect: DOMRect;
  context: { before: string; after: string };
  onApply: (newText: string) => void;
  onDismiss: () => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function PatchToolbar({
  selectedText,
  anchorRect,
  context,
  onApply,
  onDismiss,
}: PatchToolbarProps): React.ReactElement {
  const api = useApi();
  const inputRef = useRef<HTMLInputElement>(null);

  const [expanded, setExpanded] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Position the toolbar above the selection
  const toolbarStyle: React.CSSProperties = {
    position: 'fixed',
    top: `${anchorRect.top - 48}px`,
    left: `${anchorRect.left + anchorRect.width / 2}px`,
    transform: 'translateX(-50%)',
  };

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(selectedText).catch(() => {});
    onDismiss();
  }, [selectedText, onDismiss]);

  const handleDelete = useCallback(() => {
    onApply('');
  }, [onApply]);

  const handleExpandPatch = useCallback(() => {
    setExpanded(true);
    // Focus input after expand
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const handleSubmitPatch = useCallback(async () => {
    if (!prompt.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const result = await api.transform.styleCustom(selectedText, {
        name: 'patch',
        stylePrompt: `Context before: "${context.before}"\nContext after: "${context.after}"\n\nUser instruction: ${prompt}\n\nRewrite the following passage according to the instruction, maintaining coherence with surrounding context.`,
      });

      setPreview(result.text);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Patch failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [prompt, selectedText, context, api.transform]);

  const handleAcceptPreview = useCallback(() => {
    if (preview !== null) {
      onApply(preview);
    }
  }, [preview, onApply]);

  const handleRejectPreview = useCallback(() => {
    setPreview(null);
    setError(null);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (preview !== null) {
        handleAcceptPreview();
      } else {
        handleSubmitPatch();
      }
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onDismiss();
    }
  }, [preview, handleAcceptPreview, handleSubmitPatch, onDismiss]);

  return (
    <div
      className="patch-toolbar"
      style={toolbarStyle}
      onKeyDown={handleKeyDown}
    >
      {/* Preview mode */}
      {preview !== null ? (
        <div className="patch-toolbar__preview">
          <div className="patch-toolbar__preview-text">{preview}</div>
          <div className="patch-toolbar__preview-actions">
            <button
              className="patch-toolbar__btn patch-toolbar__btn--accept"
              onClick={handleAcceptPreview}
              title="Accept"
            >
              Accept
            </button>
            <button
              className="patch-toolbar__btn patch-toolbar__btn--reject"
              onClick={handleRejectPreview}
              title="Reject"
            >
              Reject
            </button>
          </div>
        </div>
      ) : expanded ? (
        /* Expanded patch input */
        <div className="patch-toolbar__input-row">
          <input
            ref={inputRef}
            className="patch-toolbar__input"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value.slice(0, 500))}
            placeholder="Describe the change..."
            maxLength={500}
            disabled={loading}
          />
          <span className="patch-toolbar__char-count">
            {prompt.length}/500
          </span>
          <button
            className="patch-toolbar__btn patch-toolbar__btn--apply"
            onClick={handleSubmitPatch}
            disabled={loading || !prompt.trim()}
          >
            {loading ? 'Patching...' : 'Apply'}
          </button>
        </div>
      ) : (
        /* Compact toolbar */
        <div className="patch-toolbar__actions">
          <button
            className="patch-toolbar__btn patch-toolbar__btn--patch"
            onClick={handleExpandPatch}
          >
            Patch...
          </button>
          <button
            className="patch-toolbar__btn"
            onClick={handleCopy}
          >
            Copy
          </button>
          <button
            className="patch-toolbar__btn patch-toolbar__btn--danger"
            onClick={handleDelete}
          >
            Delete
          </button>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="patch-toolbar__error">{error}</div>
      )}
    </div>
  );
}

export default PatchToolbar;
